"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { PageHeaderFields } from "@/types/acf";

export default function PageHeader({ data }: { data: PageHeaderFields }) {
  if (!data || data.hide) return null;

  const label = data.label ?? "";
  const title = data.title ?? "";
  const subtitle = data.subtitle ?? "";
  const text = data.text ?? "";
  const button =
    data.button && typeof data.button === "object" && data.button.url ? data.button : null;
  const logo =
    data.logo && typeof data.logo === "object" ? data.logo : null;
  const logoSrc = logo?.url ? toAbsoluteUrl(logo.url) : null;

  if (!label && !title && !subtitle && !text && !button && !logoSrc) return null;

  return (
    <div className="min-w-0 text-center flex flex-col items-center gap-4" id={data.id}>
      {/* Label pill */}
      {label && (
        <div className="inline-flex rounded-full bg-fuji-grey px-4 py-2">
          <div className="uppercase tracking-wide font-semibold text-body-2">
            <SafeHtml content={label} />
          </div>
        </div>
      )}
      {/* Title */}
      {title && (
        <h1 className="text-title-1 font-semibold">
          <SafeHtml content={title} />
        </h1>
      )}
      {/* Logo */}
      {logoSrc ? (
        <div className="max-w-full w-full flex justify-center">
          <Image
            src={logoSrc}
            alt={logo?.alt || ""}
            width={logo?.width || 1}
            height={logo?.height || 1}
            className="h-auto max-w-full w-[280px]"
            sizes="100vw"
            priority={false}
          />
        </div>
      ) : null}
      {/* Subtitle */}
      {subtitle && (
        <div className="text-title-4 font-semibold">
          <SafeHtml content={subtitle} />
        </div>
      )}
      {/* Body text */}
      {text && (
        <div className="max-w-[1000px]">
          <SafeHtml content={text} flow />
        </div>
      )}
      {/* Button with larger separation */}
      {button && (
        <div className="pt-4">
          <Button
            as="link"
            href={toAbsoluteUrl(button.url) ?? "#"}
            target={button.target || "_self"}
            rel={button.target === "_blank" ? "noopener noreferrer" : undefined}
          >
            {button.title || "Learn more"}
          </Button>
        </div>
      )}
    </div>
  );
}
